
import {DEFAULT_PLANAR_TOLERANCE_M} from "../geometry/lineIntersection2D.js";

import {T, localized} from "../i18n/index.js";
// Declarative schema for Digitalisering's user-facing settings: one source
// of truth for default values, the persisted storage shape, and the
// auto-rendered "Indstillinger" UI (see SettingsPanel.js) - adding a new
// setting later means adding one entry below, not touching multiple files.
//
// Only *user preferences* belong here (display/UX toggles a user might
// reasonably want to control). Internal algorithm parameters - RANSAC
// iterations, IRLS iterations, voxel size, inlier thresholds, seed
// neighborhood radius, hard-gate thresholds, quality-classification
// thresholds - are deliberately kept out of this schema and out of the
// settings UI entirely. Those stay owned by cylinderFit.js/pipeAnalysis.js
// as internal constants/options, not user-facing settings, unless a later,
// separate decision explicitly changes that.

// Order here is also the display order of the collapsible sub-groups inside
// the Indstillinger section - but SettingsPanel.js only ever renders a group
// that actually has at least one entry in SETTINGS_SCHEMA below (or is
// "general", which always has the reset button) - see its renderGroup()/
// mountDigitizationSettingsPanel(). Groups with no settings yet (Punkt,
// Linje, Polygon, Midtpunkt, Midterlinje / Connector) are kept here so the
// settings model/storage shape (see DigitizationSettings.js) already has a
// stable place for each tool's future settings, without showing an empty
// placeholder group in the UI in the meantime.
export const SETTINGS_GROUPS = localized([
	{id: "interaction", label: T("Interaktion")},
	{id: "general", label: T("Generelt")},
	{id: "point", label: T("Punkt")},
	{id: "line", label: T("Linje")},
	{id: "polygon", label: T("Polygon")},
	{id: "midpoint", label: T("Midtpunkt")},
	{id: "midline", label: T("Midterlinje / Connector")},
	{id: "perpendicularParallel", label: T("Vinkelret / parallel")},
	{id: "pipe", label: T("Rør")},
	{id: "vectorEditing", label: T("Vektorredigering")},
	{id: "topology", label: T("Topologi")},
]);

// `type` drives which control SettingsPanel.js's renderControl() renders -
// "boolean" (a checkbox) or "number" (a plain numeric input); add a case
// there alongside any new type introduced here.
//
// `tooltip` should explain, in plain user language: what the setting does,
// what changing it affects, and - importantly - whether it only affects
// *display* or also the registered/saved result (see task's tooltip
// requirement). Every entry here should have one unless the setting is
// genuinely self-explanatory.
export const SETTINGS_SCHEMA = localized([
	// ONE global Snap for PointView (user-facing). "interaction.snapEnabled"
	// is the single on/off: DigitizationModule.applyInteractionSettings()
	// forwards it to every AUTOMATIC snap mechanism - Digitalisering
	// (findSnapTarget), the native Measure tool (showSnap) and, via the
	// frontend, the old drawings (drawtool/geoSnap). Explicit commands
	// ("Snap til punktsky", "Snap til andet punkt") are NOT automatic
	// snapping and ignore it.
	//
	// "interaction.snapTolerancePx" is in SCREEN PIXELS, deliberately: that is
	// how Digitalisering's snap already measures (distance between the mouse
	// and a target's projected screen position), and the CAD/GIS convention
	// ("aperture"). A fixed metre value (the old 5 cm) is unusable in a 3D
	// view: zoomed out 5 cm is under a pixel, zoomed in it is hundreds of
	// pixels. The old drawings and the native Measure work in WORLD METRES
	// (fixed internal 5 cm / 15 cm) - a fundamentally different engine - so
	// pixels are not converted to metres for them; they keep their internal
	// distance and only follow the on/off switch.
	{
		key: "interaction.snapEnabled",
		group: "interaction",
		label: T("Snap"),
		type: "boolean",
		default: true,
		tooltip: T("Slår automatisk snap til/fra overalt i PointView: når musen er tæt på et eksisterende punkt, springer det nye punkt til det - i Registrering, Måling og tegningerne i sidepanelet. Slået fra placeres punkter præcis hvor du klikker i punktskyen. Skjulte registreringer snappes der aldrig til. De eksplicitte handlinger 'Snap til punktsky' og 'Snap til andet punkt' virker altid, uanset denne indstilling."),
	},
	{
		key: "interaction.snapTolerancePx",
		group: "interaction",
		label: T("Snap-afstand"),
		type: "number",
		default: 15,
		tooltip: T("Hvor tæt (i skærm-pixels) musen skal være på et eksisterende punkt for at snappe til det - i Registrering og Måling. Målt på skærmen, så følelsen er den samme uanset zoom. Gyldige værdier er 3–50 px. Tegningerne og Mål i sidepanelet arbejder i meter og bruger en fast snap-afstand; de følger kun til/fra-kontakten."),
	},
	// Surface detection: pure viewer display aid (viewer.detection ->
	// InputHandler.surfaceDetect(), a grey disc under the cursor). Moved here
	// from the sidebar/topbar so there is one settings store; the algorithm
	// itself is untouched. Default off.
	{
		key: "interaction.surfaceDetection",
		group: "interaction",
		label: T("Overfladedetektion"),
		type: "boolean",
		default: false,
		tooltip: T("Hjælper med at placere målinger og registreringer på den forreste overflade i punktskyen og reducerer risikoen for at ramme punkter bag den overflade, du arbejder på. En lille cirkel viser den fundne overflade og dens retning og er dæmpet, når overfladen ikke kan bestemmes sikkert."),
	},
	// Shared by Vinkelret punkt, Vinkelret linje og Parallelforskydning (one
	// settings group for the whole family - see task requirement). Default
	// 0 is the "no fixed distance configured" sentinel, not a real 0 m
	// default - a real localStorage-persisted number and this sentinel both
	// have to satisfy DigitizationSettings.js's typeof-based merge check
	// (see mergeWithDefaults()), which `null` (typeof "object") would fail
	// against a persisted number (typeof "number"). The distance prompt
	// itself (DistancePrompt.js) treats 0 the same as "not set" when
	// prefilling its field - the user can still type an explicit 0 per
	// registration directly in that prompt, this only affects the default.
	{
		key: "perpendicularParallel.defaultDistance",
		group: "perpendicularParallel",
		label: T("Standardafstand"),
		type: "number",
		default: 0,
		tooltip: T("Forudfylder afstandsdialogen med denne værdi, hver gang Vinkelret punkt/linje eller Parallelforskydning aktiveres. Dialogen vises stadig, og værdien kan altid ændres eller slettes for den enkelte registrering. Lad stå på 0 for ingen standardværdi."),
	},
	{
		key: "perpendicularParallel.showHelperLines",
		group: "perpendicularParallel",
		label: T("Vis hjælpelinjer"),
		type: "boolean",
		default: true,
		tooltip: T("Viser hjælpelinjer (fx den vinkelrette forbindelseslinje til basislinjen) under registrering. Påvirker kun visningen, ikke det registrerede resultat."),
	},
	{
		key: "perpendicularParallel.showDistance",
		group: "perpendicularParallel",
		label: T("Vis afstand"),
		type: "boolean",
		default: true,
		tooltip: T("Viser den aktuelle afstand til basislinjen i preview under registrering. Påvirker kun visningen."),
	},
	{
		key: "perpendicularParallel.showProjectionPoint",
		group: "perpendicularParallel",
		label: T("Vis projektionspunkt"),
		type: "boolean",
		default: true,
		tooltip: T("Viser det beregnede projektionspunkt (foden på basislinjen) under registrering. Påvirker kun visningen."),
	},
	{
		key: "perpendicularParallel.showBaselineDuringCapture",
		group: "perpendicularParallel",
		label: T("Vis basislinje under registrering"),
		type: "boolean",
		default: true,
		tooltip: T("Holder den digitaliserede basislinje synlig, mens det vinkelrette/parallelle resultat placeres. Basislinjen indgår ikke selv i det registrerede resultat."),
	},
	// The one central displacement budget every geometry-moving Vektor-
	// operation (Ret ud 2D/3D, Forenkle, Ortogonaliser, Fast vinkel,
	// Parallel/vinkelret på reference) checks its own actual computed
	// deviation against before committing - see geometry/vectorEditing.js.
	// Deliberately one setting, not one per operation (task requirement).
	// Default 0.02 m (2 cm): conservative enough that none of these
	// operations will meaningfully alter survey-grade digitized geometry
	// unless the user deliberately raises it, while still being large
	// enough to matter against typical point-cloud noise.
	{
		key: "vectorEditing.maxSagitta",
		group: "vectorEditing",
		label: T("Maksimal pilhøjde"),
		type: "number",
		default: 0.02,
		tooltip: T("Angiver hvor langt et punkt højst må flyttes eller fjernes fra den oprindelige vektorgeometri ved automatisk udretning og forenkling."),
	},
	// Only Ortogonaliser reads this (which vertices are even eligible for
	// correction) - Fast vinkel/Parallel/Vinkelret apply to whatever the
	// user explicitly selected, not to an automatically-detected set, so
	// they have no tolerance of their own. Default 5°: a commonly-cited
	// conservative threshold for automatic building/parcel-boundary
	// orthogonalization in GIS practice - tight enough that a genuinely
	// oblique corner is never silently squared off.
	{
		key: "vectorEditing.angleTolerance",
		group: "vectorEditing",
		label: T("Vinkeltolerance"),
		type: "number",
		default: 5,
		tooltip: T("Angiver hvor langt en eksisterende vinkel må afvige fra 90° eller 180°, før den medtages i ortogonaliseringen."),
	},
	// The XY-planarity tolerance every cross-feature topologi-operation
	// (Forlæng, Trim, Sæt punkt ved skæring, Saml linjer, Luk gap, delte
	// forbindelsespunkter) reads via geometry/lineIntersection2D.js's
	// classifyIntersection3D() - task section 3's requirement that an XY
	// crossing is never automatically treated as a real physical connection.
	// ("topology.verticalTolerance" - a separate Z-connectivity budget - was
	// audited and removed: it was never actually wired into
	// classifyIntersection3D()'s live call sites, which either hardcode
	// Infinity (a pure XY/plan operation - Z never rejects) or fall back to
	// their own internal VERTICAL_CONNECTION_TOLERANCE_M constant, so the
	// setting silently did nothing regardless of its value - a dead,
	// misleading control rather than a real one.)
	{
		key: "topology.planarTolerance",
		group: "topology",
		label: T("Plan-tolerance (XY)"),
		type: "number",
		default: DEFAULT_PLANAR_TOLERANCE_M,
		tooltip: T("Hvor tæt to geometrier skal være i plan (XY), inkl. bånd ud over en linjes eget endepunkt, for at tælle som en reel skæring/forbindelse ved Forlæng, Trim, Sæt punkt ved skæring, Saml linjer og registrering af delte forbindelsespunkter."),
	},
]);

// Builds the default settings tree, shaped {groupId: {leafKey: value}} for
// every group in SETTINGS_GROUPS (even ones with no settings yet, so
// DigitizationSettingsStore always has a stable object to merge into/reset
// against).
export function buildDefaults(){
	const defaults = {};
	for(const group of SETTINGS_GROUPS) defaults[group.id] = {};
	for(const def of SETTINGS_SCHEMA){
		const [groupId, leafKey] = def.key.split(".");
		defaults[groupId][leafKey] = def.default;
	}
	return defaults;
}

export function settingsInGroup(groupId){
	return SETTINGS_SCHEMA.filter((def) => def.group === groupId);
}

// Pure "which groups should the Indstillinger UI actually render" logic,
// pulled out of SettingsPanel.js so it's Level 1 (no DOM/jQuery) testable -
// see CLAUDE.md's Test-hierarki. A group renders only if it declares at
// least one setting in SETTINGS_SCHEMA, or is "general" (which always has
// the reset-everything action even when it has no other settings yet) - task
// requirement: no empty placeholder groups in the UI.
export function visibleSettingsGroups(){
	return SETTINGS_GROUPS.filter((group) => group.id === "general" || settingsInGroup(group.id).length > 0);
}
